import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TagOportunidade } from '../../../../../shared/services/models/vendas/oportunidade.model';

export interface TagsOportunidadeData {
  titulo: string;
  /** Catálogo real de tag_crm, com a cor do cadastro. */
  disponiveis: TagOportunidade[];
  selecionadas: number[];
}

/**
 * Tags do card pelo menu do quadro (US §2 — "tags (abre o painel de tags do card)"). Segue o diálogo
 * padrão "Alterar tags" do sistema (o do Processo de Venda): cabeçalho com fechar, busca, lista com
 * checkbox e Cancelar/Aplicar. Sem o seletor Incluir/Remover/Substituir, que serve à alteração em
 * lote: aqui é um card só, e o que fica marcado é o que ele passa a ter.
 */
@Component({
  selector: 'app-tags-oportunidade-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule,
    MatIconModule, MatInputModule],
  template: `
    <div class="modal-container">
      <div class="tod-cabecalho">
        <h2 mat-dialog-title class="dialog-title">
          <mat-icon>label</mat-icon>
          <span>Alterar tags</span>
        </h2>
        <button mat-icon-button type="button" aria-label="Fechar" (click)="cancelar()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div mat-dialog-content class="dialog-content">
        <p class="question-label">Oportunidade: <b>{{ data.titulo }}</b></p>

        <div class="tags-header">
          <span class="tags-label">Selecione as tags</span>
          @if (selecionadas.size > 0) {
            <span class="status-badge badge-vermelho">{{ contadorText }}</span>
          }
        </div>

        <mat-form-field appearance="outline" class="tod-campo">
          <mat-label>Buscar tags...</mat-label>
          <mat-icon matPrefix>search</mat-icon>
          <input matInput [(ngModel)]="searchText" (ngModelChange)="filtrarTags()"
            [disabled]="data.disponiveis.length === 0" />
        </mat-form-field>

        <div class="tag-list">
          @for (tag of tagsFiltradas; track tag.idTagCrm) {
            <div class="tag-item" (click)="alternar(tag.idTagCrm)">
              <mat-checkbox [checked]="selecionadas.has(tag.idTagCrm)" (click)="$event.stopPropagation()"
                (change)="alternar(tag.idTagCrm)">
                <span class="tag-label">
                  <span class="tag-cor" [style.background-color]="tag.cor"></span>
                  {{ tag.descricao }}
                </span>
              </mat-checkbox>
            </div>
          }

          @if (data.disponiveis.length === 0) {
            <p class="empty-tags">Nenhuma tag de CRM cadastrada. Cadastre em Configurações › Vendas › Tags de CRM.</p>
          } @else if (tagsFiltradas.length === 0) {
            <p class="empty-tags">Nenhuma tag encontrada.</p>
          }
        </div>
      </div>

      <div mat-dialog-actions align="end" class="mat-mdc-dialog-actions">
        <button mat-stroked-button type="button" class="btn-secondary" (click)="cancelar()">Cancelar</button>
        <button mat-flat-button type="button" class="btn-primary" [disabled]="data.disponiveis.length === 0"
          (click)="salvar()">Aplicar</button>
      </div>
    </div>
  `,
  styles: [`
    .modal-container { display: flex; flex-direction: column; height: 100%; }

    /* Prefixado: .modal-header é classe global do styles.css. */
    .tod-cabecalho {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 1.5rem;
      border-bottom: 1px solid #e0e0e0;
      flex-shrink: 0;
    }

    .dialog-title {
      display: flex !important;
      align-items: center !important;
      gap: 0.625rem;
      font-size: 1.1rem;
      font-weight: 500;
      margin: 0 !important;
      padding: 0 !important;
    }

    .dialog-title mat-icon { color: var(--fg-muted); margin: 0; display: flex; align-items: center; justify-content: center; }

    .question-label { margin-bottom: 1rem; }
    .tod-campo { width: 100%; }

    .tags-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.25rem; }
    .tags-label { font-size: 0.875rem; font-weight: 500; }

    .tag-list { max-height: 16.25rem; overflow-y: auto; border: 1px solid #e0e0e0; border-radius: 0.5rem; }
    .tag-item { padding: 0.375rem 0.75rem; cursor: pointer; border-bottom: 1px solid #f0f0f0; }
    .tag-item:last-child { border-bottom: none; }
    .tag-item:hover { background-color: #fafafa; }
    .tag-label { display: inline-flex; align-items: center; gap: 8px; }
    .tag-cor { display: inline-block; width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }

    .empty-tags { padding: 1.25rem 1rem; text-align: center; font-size: 0.85rem; color: var(--fg-subtle); margin: 0; }

    :host-context(body.dark-mode) .tod-cabecalho,
    :host-context(body.dark-mode) .tag-list,
    :host-context(body.dark-mode) .tag-item { border-color: #49454f; }
    :host-context(body.dark-mode) .tag-item:hover { background-color: #2b2930; }
  `],
})
export class TagsOportunidadeDialogComponent {
  selecionadas: Set<number>;
  searchText = '';
  tagsFiltradas: TagOportunidade[];

  constructor(
    public dialogRef: MatDialogRef<TagsOportunidadeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TagsOportunidadeData
  ) {
    this.selecionadas = new Set(data.selecionadas);
    this.tagsFiltradas = [...data.disponiveis];
  }

  get contadorText(): string {
    const n = this.selecionadas.size;
    return n === 1 ? '1 tag selecionada' : `${n} tags selecionadas`;
  }

  filtrarTags(): void {
    const termo = this.searchText.toLowerCase().trim();
    this.tagsFiltradas = termo
      ? this.data.disponiveis.filter(t => t.descricao.toLowerCase().includes(termo))
      : [...this.data.disponiveis];
  }

  alternar(idTagCrm: number): void {
    if (this.selecionadas.has(idTagCrm)) this.selecionadas.delete(idTagCrm);
    else this.selecionadas.add(idTagCrm);
  }

  salvar(): void {
    // Preserva a ordem do catálogo, para o card não embaralhar os chips a cada edição.
    this.dialogRef.close(
      this.data.disponiveis.filter(t => this.selecionadas.has(t.idTagCrm)).map(t => t.idTagCrm)
    );
  }

  cancelar(): void {
    this.dialogRef.close(null);
  }
}
